import { type Dispatch } from '@reduxjs/toolkit';
import React, { memo, Suspense, useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { connect } from 'react-redux';

import { type IAccountState } from '../../reducers/account/types';
import { type RootState } from '../../reducers/customer';

const SignInModal = React.lazy(() => import('../sign-in-modal'));
const GiftRegistryPickerModal = React.lazy(
  () => import('./gift-registry-picker-modal'),
);

interface IProps {
  productUUID: string;
  Account: IAccountState;
  dispatch: Dispatch;
}

const AddToGiftRegistry = memo(
  ({ productUUID, Account }: IProps): React.JSX.Element => {
    const { t } = useTranslation();
    const [showSignIn, setShowSignIn] = useState<boolean>(false);
    const [showPicker, setShowPicker] = useState<boolean>(false);

    const handleClick = useCallback((): void => {
      if (Account.isSignedIn) {
        setShowPicker(true);
      } else {
        setShowSignIn(true);
      }
    }, [Account.isSignedIn]);

    return (
      <>
        <button
          type="button"
          className="product-button product-button--secondary"
          onClick={handleClick}
        >
          {t('product.giftRegistry.addButton')}
        </button>

        {showSignIn && !Account.isSignedIn && (
          <Suspense fallback={null}>
            <SignInModal onClose={(): void => setShowSignIn(false)} />
          </Suspense>
        )}

        {showPicker && Account.isSignedIn && (
          <Suspense fallback={null}>
            <GiftRegistryPickerModal
              productUUID={productUUID}
              onClose={(): void => setShowPicker(false)}
            />
          </Suspense>
        )}
      </>
    );
  },
);

AddToGiftRegistry.displayName = 'AddToGiftRegistry';

const mapStateToProps = (
  { Account }: RootState,
  ownProps: { productUUID: string },
): { Account: IAccountState; productUUID: string } => ({
  Account,
  ...ownProps,
});

export default connect(mapStateToProps)(AddToGiftRegistry);
